import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';

import { Sortable } from './sortable';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FractionalItem extends Sortable<string> {}

export function moveFractionalItem<T extends FractionalItem>(
  fromItem: T,
  toItem: T,
  items: readonly T[],
): T[] {
  const fromIndex = items.indexOf(fromItem);
  const toIndex = items.indexOf(toItem);
  const direction = fromIndex < toIndex ? 1 : -1;
  const toIndex2 = toIndex + direction;
  const aIndex = Math.min(toIndex, toIndex2);
  const bIndex = Math.max(toIndex, toIndex2);
  const fromOrder = fromItem.order ?? null;
  let aOrder = items[aIndex]?.order ?? null;
  let bOrder = items[bIndex]?.order ?? null;

  let updatedFromIndex = 0;
  let updatedRows = [{ ...fromItem }];

  if ((aOrder === null || fromOrder === null) && bOrder === null) {
    // Generate new indexes until max(aIndex, bIndex, fromIndex)
    const lastIndex = Math.min(Math.max(aIndex, bIndex, fromIndex), items.length - 1);
    const firstIndex = items.findLastIndex((row) => !!row.order);

    // If firstIndex is the last row - order state is broken and we regenerate new indexes for all rows
    if (firstIndex === items.length - 1) {
      fixAllKeys();
    } else {
      const offsetIndex = firstIndex + 1;
      const lastOrder = items[firstIndex]?.order ?? null;
      const totalIndexes = Math.min(lastIndex - offsetIndex + 1, items.length);

      const orders = generateNKeysBetween(lastOrder, null, totalIndexes);
      updatedRows = orders.map(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (order, i) => ({ ...items[i + offsetIndex]!, order }),
      );
      const allOrders = items.map((item, i) =>
        i >= offsetIndex ? updatedRows[i - offsetIndex]?.order : item.order,
      );

      aOrder = allOrders[aIndex] ?? null;
      bOrder = allOrders[bIndex] ?? null;
      updatedFromIndex = Math.max(fromIndex - offsetIndex, 0);

      if (offsetIndex > fromIndex) {
        updatedFromIndex = 0;
        updatedRows.unshift({ ...fromItem });
      }
    }
  }

  let order: string;
  try {
    order = generateKeyBetween(aOrder, bOrder);
  } catch {
    // Try to fix all keys if invalid keys detected
    fixAllKeys();
    order = generateKeyBetween(aOrder, bOrder);
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  updatedRows[updatedFromIndex]!.order = order;

  return updatedRows;

  function fixAllKeys() {
    const orders = generateNKeysBetween(null, null, items.length);
    updatedRows = items.map((item, i) => ({ ...item, order: orders[i] }));
    aOrder = updatedRows[aIndex]?.order ?? null;
    bOrder = updatedRows[bIndex]?.order ?? null;
    updatedFromIndex = fromIndex;
  }
}

export function nextFractionalOrder(lastOrder?: FractionalItem['order']) {
  if (!lastOrder) {
    return;
  }

  return generateKeyBetween(lastOrder, null);
}

export function fractionalOrderBetween(a?: FractionalItem, b?: FractionalItem) {
  if (!a?.order && !b?.order) {
    return;
  }

  return generateKeyBetween(a?.order ?? null, b?.order ?? null);
}
